import React, { useState, useRef, useMemo } from 'react';
import { motion, useInView } from 'framer-motion';
import { Container, Row, Col, Button } from 'react-bootstrap';
import styles from './Services.module.css';

const Services = ({ navigateTo }) => {
    const [activeService, setActiveService] = useState(null);
    const [expandedCards, setExpandedCards] = useState({});
    const ref = useRef(null);
    const isInView = useInView(ref, { once: true, margin: "-100px" });

    const services = useMemo(() => [
        {
            id: 'structural-analysis',
            title: 'Analyse Structurale',
            subtitle: 'Conception et calculs avancés',
            icon: '/icons/svc-analyse.svg',
            color: '#007bff',
            gradient: 'linear-gradient(135deg, #007bff, #0056b3)',
            image: '/images/utils/Services%20-%20Analyse%20Structurale.png',
            description: 'Notre équipe maîtrise les particularités de l\'analyse structurale dans la réalisation de projets résidentiels, commerciaux et industriels.',
            features: [
                'Calculs de charges et contraintes',
                'Modélisation 3D avancée',
                'Optimisation des structures',
                'Conformité aux codes du bâtiment',
                'Analyse sismique et dynamique'
            ]
        },
        {
            id: 'steel-framework',
            title: 'Charpentes Métalliques',
            subtitle: 'Structures en acier haute performance',
            icon: '/icons/svc-metal.svg',
            color: '#28a745',
            gradient: 'linear-gradient(135deg, #28a745, #1e7e34)',
            image: '/images/utils/Services%20-%20Charpente%20m%C3%A9tallique.png',
            description: 'Les charpentes métalliques permettent des portées impressionnantes tout en offrant une rapidité d\'assemblage exceptionnelle.',
            features: [
                'Conception de structures métalliques',
                'Calculs de stabilité',
                'Détails de connexions',
                'Plans d\'atelier et de montage',
                'Supervision de fabrication'
            ]
        },
        {
            id: 'concrete-structures',
            title: 'Structures Béton Armé',
            subtitle: 'Béton haute résistance et durabilité',
            icon: '/icons/svc-beton.svg',
            color: '#ffc107',
            gradient: 'linear-gradient(135deg, #ffc107, #e0a800)',
            image: '/images/utils/Services%20-%20Structure%20en%20b%C3%A9ton.png',
            description: 'Spécialistes en structures béton armé pour tous types de projets, de la résidence aux infrastructures complexes.',
            features: [
                'Poteau, dalle et poutres',
                'Conception de dalles et poutres',
                'Calculs de fondations',
                'Murs de soutènement'
            ]
        },
        {
            id: 'wood-structures',
            title: 'Structures Bois',
            subtitle: 'Construction durable et écologique',
            icon: '/icons/svc-bois.svg',
            color: '#dc3545',
            gradient: 'linear-gradient(135deg, #dc3545, #bd2130)',
            image: '/images/utils/Services%20-%20bois.png',
            description: 'L\'utilisation du bois en construction favorise le développement durable et permet l\'usage de matériaux locaux.',
            features: [
                'Conception en Bois d\'ingénierie',
                'Ossatures bois multi-étages',
                'Calculs de résistance au feu',
                'Détails d\'assemblages',
                'Ossature légère bois multi-étage'
            ]
        },
        {
            id: 'bim-modeling',
            title: 'Modélisation 3D et BIM',
            subtitle: 'Technologies de pointe',
            icon: '/icons/svc-bim.svg',
            color: '#6f42c1',
            gradient: 'linear-gradient(135deg, #6f42c1, #59359a)',
            image: '/images/utils/Services%20-%20BIM.png',
            description: 'Tous nos projets sont modélisés en REVIT, constituant un avantage considérable pour la livraison rapide des projets.',
            features: [
                'Modélisation 3D complète',
                'Coordination multidisciplinaire',
                'Détection de conflits',
                'Métrés automatisés'
            ]
        },
        {
            id: 'inspection-expertise',
            title: 'Maintien d\'actif',
            subtitle: 'Diagnostic et évaluation',
            icon: '/icons/svc-inspection.svg',
            color: '#17a2b8',
            gradient: 'linear-gradient(135deg, #17a2b8, #138496)',
            image: '/images/utils/Services%20-%20Maintien%20des%20actifs.png',
            description: 'Services complets d\'inspection, d\'évaluation structurale et d\'expertise technique pour tous types d\'ouvrages.',
            features: [
                'Inspection de structures existantes',
                'Évaluation de capacité portante',
                'Diagnostic de pathologies',
                'Rapports d\'expertise',
                'Recommandations technique',
                'Inspection de façade et stationnement (loi 122)',
                'Inspection et expertise skatepark',
                'Transformation patinoire pour accessibilité universelle',
                'Réhabilitation des structures'
            ]
        },
        {
            id: 'civil',
            title: 'Civil',
            subtitle: 'Infrastructures et ouvrages civils',
            icon: '/icons/svc-civil.svg',
            color: '#20c997',
            gradient: 'linear-gradient(135deg, #20c997, #12876e)',
            image: '/images/utils/Services%20-%20G%C3%A9nie%20Civil.png',
            description: 'Conception et calcul de structures civiles, notamment les ponceaux et ouvrages de drainage pour tous types de projets.',
            features: [
                'Conception structure ponceau',
                'Calculs hydrauliques et structuraux',
                'Ouvrages de drainage et rétention',
                'Infrastructures routières',
                'Conformité aux normes BNQ et MTQ'
            ]
        },
        {
            id: 'cost-estimation',
            title: 'Estimation des coûts',
            subtitle: 'Budget précis et contrôle des coûts',
            icon: '/icons/svc-cout.svg',
            color: '#fd7e14',
            gradient: 'linear-gradient(135deg, #fd7e14, #d45f00)',
            image: 'https://images.unsplash.com/photo-1554224155-6726b3ff858f?ixlib=rb-4.0.3&auto=format&fit=crop&w=800&q=80',
            description: 'Services d\'estimation détaillée pour une planification financière réaliste de vos projets.',
            features: [
                'Estimation détaillée des coûts de construction',
                'Analyse des soumissions et devis',
                'Suivi budgétaire en cours de projet',
                'Révision de contrats et engagements financiers',
                'Optimisation des coûts sans compromis qualité'
            ]
        }
    ], []);

    // Services à afficher : dérivé directement de activeService (pas d'état séparé)
    const displayedServices = activeService !== null ? [services[activeService]] : services;

    const handleServiceSelect = (index) => {
        if (index === null || activeService === index) {
            setActiveService(null);
        } else {
            setActiveService(index);
        }
        setExpandedCards({});
    };

    const toggleCard = (serviceId) => {
        setExpandedCards(prev => ({
            ...prev,
            [serviceId]: !prev[serviceId]
        }));
    };

    const containerVariants = {
        hidden: {
            opacity: 0,
            transition: {
                staggerChildren: 0.05,
                staggerDirection: -1
            }
        },
        visible: {
            opacity: 1,
            transition: {
                staggerChildren: 0.08,
                delayChildren: 0.1,
                duration: 0.3
            }
        }
    };

    const cardVariants = {
        hidden: {
            opacity: 0,
            y: 50,
            scale: 0.95
        },
        visible: {
            opacity: 1,
            y: 0,
            scale: 1,
            transition: {
                type: "spring",
                stiffness: 120,
                damping: 15,
                duration: 0.4
            }
        }
    };

    return (
        <section id="services" className={styles.servicesSection} ref={ref} style={{ paddingTop: '120px' }}>
            {/* En-tête de section */}
            <Container>
                <motion.div
                    className={styles.servicesHeader}
                    initial={{ opacity: 0, y: 50 }}
                    animate={isInView ? { opacity: 1, y: 0 } : {}}
                    transition={{ duration: 0.8 }}
                >
                    <div className={styles.servicesEyebrow}>
                        <span className={styles.eyebrowLine}></span>
                        <span className={styles.eyebrowText}>DHR Structures | Services et Expertise</span>
                        <span className={styles.eyebrowDot}></span>
                    </div>
                    <h2 className={styles.servicesTitle}>
                        Expertise en<br />
                        <span className={styles.highlightBlue}>Génie Structural</span>
                    </h2>
                    <div className={styles.servicesIntro}>
                        <p className={styles.leadDescription}>
                            Au fil des projets et de formations spécialisées, nous avons développé une expertise solide en conception
                            et construction dans les secteurs résidentiels, commerciaux, industriels et institutionnels.
                        </p>
                        <p className={styles.secondaryDescription}>
                            Notre équipe est en mesure de prendre en charge la conception des plans et devis, l'estimation des coûts
                            et la supervision des travaux pour constructions neuves, agrandissements, réhabilitation ou changements d'usage.
                        </p>
                    </div>
                </motion.div>

                {/* Navigation des services */}
                <motion.div
                    className={styles.servicesNavigation}
                    initial={{ opacity: 0, y: 30 }}
                    animate={isInView ? { opacity: 1, y: 0 } : {}}
                    transition={{ duration: 0.8, delay: 0.2 }}
                >
                    <div className={styles.navSubtitle}>
                        DHR Structures offre des services d'ingénierie dans les domaines suivants :
                        {activeService !== null && (
                            <button
                                onClick={() => handleServiceSelect(null)}
                                className={styles.showAllBtn}
                            >
                                <i className="bi bi-grid"></i>
                                Afficher tous les services
                            </button>
                        )}
                    </div>
                    <div className={styles.servicesNavGrid}>
                        {services.map((service, index) => (
                            <motion.div
                                key={service.id}
                                className={`${styles.serviceNavItem} ${activeService === index ? styles.active : ''}`}
                                onClick={() => handleServiceSelect(index)}
                                whileHover={{ scale: 1.02, y: -2 }}
                                whileTap={{ scale: 0.98 }}
                            >
                                <div className={styles.navItemContent}>
                                    <img src={service.icon} alt={service.title} style={{ width: '22px', height: '22px', objectFit: 'contain' }} />
                                    <span>{service.title}</span>
                                </div>
                                <div className={styles.expandIcon}>
                                    <i className={`bi ${activeService === index ? 'bi-dash' : 'bi-plus'}`}></i>
                                </div>
                            </motion.div>
                        ))}
                    </div>
                </motion.div>

                {/* Cartes de services */}
                <motion.div
                    className={styles.servicesGrid}
                    key={`grid-${activeService}`}
                    variants={containerVariants}
                    initial="hidden"
                    animate={isInView ? "visible" : "hidden"}
                >
                    <Row className={activeService !== null ? 'justify-content-center' : ''}>
                        {displayedServices.map((service) => (
                            <Col lg={activeService !== null ? 8 : 6} key={service.id} className={styles.serviceCardCol}>
                                <motion.div
                                    className={`${styles.serviceCard} ${expandedCards[service.id] ? styles.expanded : ''}`}
                                    variants={cardVariants}
                                    whileHover={{ y: -8, transition: { duration: 0.2 } }}
                                    whileTap={{ scale: 0.98 }}
                                >
                                    <div className={styles.serviceCardImage}>
                                        <img src={service.image} alt={service.title} />
                                        <div className={styles.imageOverlay} style={{ background: service.gradient }}></div>
                                        <div className={styles.serviceIcon} style={{ background: service.color }}>
                                            <img src={service.icon} alt={service.title} style={{ width: '36px', height: '36px', objectFit: 'contain', filter: 'brightness(0) invert(1)' }} />
                                        </div>
                                    </div>

                                    <div className={styles.serviceCardContent}>
                                        <div className={styles.serviceHeader}>
                                            <h3 className={styles.serviceTitle}>{service.title}</h3>
                                            <p className={styles.serviceSubtitle}>{service.subtitle}</p>
                                        </div>

                                        <p className={styles.serviceDescription}>{service.description}</p>

                                        <div className={`${styles.serviceFeatures} ${expandedCards[service.id] ? styles.visible : ''}`}>
                                            <ul>
                                                {service.features.map((feature, idx) => (
                                                    <motion.li
                                                        key={`${service.id}-feature-${idx}`}
                                                        initial={{ opacity: 0, x: -20 }}
                                                        animate={expandedCards[service.id] ? {
                                                            opacity: 1,
                                                            x: 0,
                                                            transition: { delay: idx * 0.05, duration: 0.3 }
                                                        } : { opacity: 0, x: -20 }}
                                                    >
                                                        <i className="bi bi-check-circle" style={{ color: service.color }}></i>
                                                        {feature}
                                                    </motion.li>
                                                ))}
                                            </ul>
                                        </div>

                                        <div className={styles.serviceActions}>
                                            <Button
                                                className={styles.expandBtn}
                                                onClick={() => toggleCard(service.id)}
                                                style={{ borderColor: service.color, color: service.color, '--btn-color': service.color }}
                                            >
                                                {expandedCards[service.id] ? 'Voir moins' : 'En savoir plus'}
                                                <i className={`bi bi-chevron-${expandedCards[service.id] ? 'up' : 'down'}`}></i>
                                            </Button>
                                        </div>
                                    </div>
                                </motion.div>
                            </Col>
                        ))}
                    </Row>
                </motion.div>

                {/* Section CTA */}
                <motion.div
                    className="section-cta"
                    initial={{ opacity: 0, y: 40 }}
                    animate={isInView ? { opacity: 1, y: 0 } : {}}
                    transition={{ duration: 0.8, delay: 0.6 }}
                >
                    <div className="section-cta-inner">
                        <h3>Besoin d'une consultation ?</h3>
                        <p>Nos experts sont à votre disposition pour discuter de votre projet et vous proposer les meilleures solutions.</p>
                        <div className="section-cta-buttons">
                            <button className="section-cta-btn-outline" onClick={() => navigateTo('projects')}>
                                NOS DOMAINES D'INTERVENTION
                            </button>
                            <button className="section-cta-btn-solid" onClick={() => navigateTo('contact')}>
                                NOUS CONTACTER
                            </button>
                        </div>
                    </div>
                </motion.div>
            </Container>
        </section>
    );
};

export default Services;